import { SceneSlot, RoughCutPlayer } from "./SceneSlot";
import React from "react";
// Filmstrip.tsx — the run as pictures first (session 3 G5; VMS-OPERATOR-UI §3–4).
//
// One data source: GET /ops/runs/:runId/filmstrip (opsApi.filmstrip) — beats grouped by segment, each with its previews
// (by the artefact's CURRENT url), production plane and run overlay; the run's parked gate; the animatic.
//
// Semantics (session-3 update 1):
//   · Approve / Reject is for the RUN's parked gate — on the contact sheet, never on a beat (gates are per run).
//   · A beat card carries a per-beat OK / needs re-roll MARK; the sheet aggregates the marks beside Approve. Marks are
//     the reviewer's working notes in this view — they are not persisted and decide nothing.
//   · Re-roll = regenerate the LAYER SHOWN: a frame (before the storyboard gate is decided → the bounded review
//     regenerate, never past the gate) or a clip (→ retake take k and the takes chained after it).
//   · The inspector's re-render actions stay, behind a "More" menu.
//   · Edit (words / direction) and Drop (remove a beat, continuity re-plans) are NOT here yet: the respecify paths they
//     need do not exist below the UI (see AUDIT-G5) — no button that cannot work.
import type { Filmstrip, FilmstripBeat, Preview } from "../../api/ops.ts";

export type Mark = "ok" | "reroll";
const STATUS_COLOR: Record<string, string> = {
  blocked: "#9ca3af", ready: "#2563eb", dispatched: "#7c3aed", running: "#7c3aed", awaiting_gate: "#d97706",
  succeeded: "#16a34a", failed: "#dc2626", skipped: "#a3a3a3", done: "#16a34a",
};
const money = (c: number) => `$${(c / 100).toFixed(2)}`;
const pill = (bg: string): React.CSSProperties =>
  ({ background: bg, color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 11, whiteSpace: "nowrap" });
const small: React.CSSProperties = { padding: "3px 8px", borderRadius: 6, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", fontSize: 12 };

/** The layer a card shows — the most advanced artefact that exists: clip, else frame, else card. Pure. */
export function layerOf(b: FilmstripBeat): { kind: "clip" | "frame" | "card" | "none"; preview: Preview | null } {
  if (b.production?.clip?.url) return { kind: "clip", preview: b.previews.clip };
  if (b.production?.image?.url) return { kind: "frame", preview: b.previews.frame };
  if (b.previews.card) return { kind: "card", preview: b.previews.card };
  return { kind: "none", preview: null };
}

/** Operator visuals G1/G2: every layer this beat HAS, most advanced first — clip, frame, end frame, card. Pure. */
export function layersOf(b: FilmstripBeat): { kind: "clip" | "frame" | "end" | "card"; preview: Preview | null; url: string | null }[] {
  const out: { kind: "clip" | "frame" | "end" | "card"; preview: Preview | null; url: string | null }[] = [];
  if (b.production?.clip?.url) out.push({ kind: "clip", preview: b.previews.clip, url: b.production.clip.url });
  if (b.production?.image?.url) out.push({ kind: "frame", preview: b.previews.frame, url: b.production.image.url });
  if (b.production?.endImage?.url || b.previews.endFrame) out.push({ kind: "end", preview: b.previews.endFrame, url: b.production?.endImage?.url ?? null });
  if (b.production?.card?.url || b.previews.card) out.push({ kind: "card", preview: b.previews.card, url: b.production?.card?.url ?? null });
  return out;
}

/** Operator visuals G3: the verify steps' verdicts on this beat, as badges (latest attempt per step). Pure. */
export function verdictsOf(b: FilmstripBeat): { stepId: string; passed: boolean | null; defect: string | null; score: number | null }[] {
  const latest = new Map<string, NonNullable<FilmstripBeat["run"]>["steps"][number]>();
  for (const s of b.run?.steps ?? []) if (s.passed !== undefined && s.passed !== null || /_verify$/.test(s.stepId)) latest.set(s.stepId, s);
  return [...latest.values()].filter((s) => s.passed != null).map((s) => ({ stepId: s.stepId, passed: s.passed ?? null, defect: s.defect ?? null, score: s.score }));
}

/** Operator visuals G4: this beat's slice of the voice track as a media fragment (#t=start,end), or null. Pure. */
export function voiceSliceOf(b: FilmstripBeat, voice: Filmstrip["document"]["voice"]): string | null {
  const p = b.production;
  if (!voice || !p) return null;
  if (voice.conformedUrl && p.conformedStartMs != null && p.conformedEndMs != null && p.conformedEndMs > p.conformedStartMs)
    return `${voice.conformedUrl}#t=${(p.conformedStartMs / 1000).toFixed(2)},${(p.conformedEndMs / 1000).toFixed(2)}`;
  if (voice.url && p.sliceStartMs != null && p.sliceMs != null && p.sliceMs > 0)
    return `${voice.url}#t=${(p.sliceStartMs / 1000).toFixed(2)},${((p.sliceStartMs + p.sliceMs) / 1000).toFixed(2)}`;
  return null;
}

const shortStep = (id: string) => id.replace(/_verify$/, "").replace(/^viral_dna_?/, "viral ").replace(/_/g, " ");

/** Operator visuals G5 + G4: what was made — the delivered video (and post-production's re-stitch), and the voice
 *  track — at the top of the run view, so the final decision has the thing itself to look at. */
export function DeliveredMedia({ film }: { film: Filmstrip }) {
  const d = film.document;
  if (!d.render?.url && !d.postRender?.url && !d.voice?.url && !d.voice?.conformedUrl) return null;
  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 12, padding: 10, background: "#f9fafb", borderRadius: 8 }}>
      {d.render?.url && <figure style={{ margin: 0 }} data-visual>
        <video src={d.render.url} poster={d.render.preview?.posterUrl} controls preload="metadata" style={{ height: 320, borderRadius: 6, background: "#000" }} />
        <figcaption style={{ fontSize: 12, color: "#6b7280" }}>rendered video{d.render.durationSec ? ` · ${d.render.durationSec.toFixed(1)}s` : ""}</figcaption></figure>}
      {d.postRender?.url && <figure style={{ margin: 0 }} data-visual>
        <video src={d.postRender.url} controls preload="metadata" style={{ height: 320, borderRadius: 6, background: "#000" }} />
        <figcaption style={{ fontSize: 12, color: "#6b7280" }}>finished cut</figcaption></figure>}
      {(d.voice?.conformedUrl || d.voice?.url) && <figure style={{ margin: 0, alignSelf: "center" }}>
        <audio src={(d.voice.conformedUrl ?? d.voice.url)!} controls preload="none" />
        <figcaption style={{ fontSize: 12, color: "#6b7280" }}>voice track{d.voice.conformedUrl ? " (conformed)" : ""}</figcaption></figure>}
    </div>
  );
}

/** The marks tally as a gate decision's `note` — so the decision span records the review ("n OK · m need re-roll")
 *  without the marks becoming persisted state. Pure. */
export function marksNote(c: { ok: number; reroll: number; unmarked: number }): string {
  return `contact sheet: ${c.ok} OK · ${c.reroll} need re-roll · ${c.unmarked} unmarked`;
}

/** Aggregate the marks for the sheet. Pure. */
export function markCounts(beats: FilmstripBeat[], marks: Record<string, Mark | undefined>) {
  const ok = beats.filter((b) => marks[b.beatId] === "ok").length;
  const reroll = beats.filter((b) => marks[b.beatId] === "reroll").length;
  return { ok, reroll, unmarked: beats.length - ok - reroll };
}

export function BeatCard(p: {
  beat: FilmstripBeat;
  mark?: Mark;
  onMark?: (m: Mark | undefined) => void;
  /** Re-roll the layer shown. Absent → no re-roll offered. */
  onReroll?: (layer: "frame" | "clip") => void;
  /** The inspector's re-render actions, behind "More". */
  more?: { label: string; run: () => void }[];
  busy?: boolean;
  /** Operator visuals G4: the document's voice track — the card plays this beat's slice of it. */
  voice?: Filmstrip["document"]["voice"];
  /** §4b: this scene is the one the rough cut is playing. */
  active?: boolean;
}) {
  const { beat, mark } = p;
  const all = layersOf(beat);
  const [pick, setPick] = React.useState<string | null>(null);
  const chosen = all.find((l) => l.kind === pick) ?? all[0];
  const layer = chosen ? { kind: chosen.kind === "end" ? "frame" as const : chosen.kind, preview: chosen.preview } : layerOf(beat);
  const verdicts = verdictsOf(beat);
  const failedVerdict = verdicts.some((v) => v.passed === false);
  const slice = p.voice ? voiceSliceOf(beat, p.voice) : null;
  const o = beat.run;
  const score = o?.steps.map((s) => s.score).filter((x): x is number => x != null).slice(-1)[0];
  const cardRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => { if (p.active) cardRef.current?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" }); }, [p.active]);
  return (
    <div ref={cardRef} style={{ boxShadow: p.active ? "0 0 0 3px #2563eb" : undefined, width: 188, border: `2px solid ${mark === "ok" ? "#16a34a" : mark === "reroll" ? "#dc2626" : failedVerdict ? "#f87171" : "#e5e7eb"}`,
      borderRadius: 8, padding: 6, background: "#fff", fontSize: 12 }}>
      <div style={{ position: "relative", width: "100%", aspectRatio: "9 / 16", maxHeight: 280, background: "#f3f4f6", borderRadius: 4, overflow: "hidden" }}>
        {/* The thumbnail when there is one, else the STORED artefact itself — a frame / card whose thumbnail was never
            made showed "no preview yet" over an image that existed (run eda0b9ff, 2026-09-28). A clip without a
            poster plays muted from its own URL. */}
        {/* §4b: by default ONE animated slot per scene (SceneSlot); picking a layer below shows that layer still */}
        {pick === null ? <SceneSlot beat={beat} voice={p.voice} /> : layer.preview
          ? <img src={layer.preview.posterUrl} alt={`${beat.beatId} ${layer.kind}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : chosen?.url && layer.kind === "clip"
            ? <video src={chosen.url} muted playsInline preload="metadata" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : chosen?.url
              ? <img src={chosen.url} alt={`${beat.beatId} ${layer.kind}`} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <div style={{ padding: 8, color: "#9ca3af" }}>{layer.kind === "none" ? "nothing yet" : `${layer.kind} — no preview yet`}</div>}
        <span style={{ position: "absolute", top: 4, left: 4, ...pill("#111827") }}>{chosen?.kind === "end" ? "end frame" : layer.kind}</span>
        {verdicts.length > 0 && (
          <div style={{ position: "absolute", bottom: 4, left: 4, right: 4, display: "flex", gap: 3, flexWrap: "wrap" }}>
            {verdicts.map((v) => (
              <span key={v.stepId} title={`${v.stepId}${v.score != null ? ` · ${v.score.toFixed(2)}` : ""}${v.defect ? ` — ${v.defect}` : ""}`}
                style={pill(v.passed ? "#15803d" : "#b91c1c")}>{v.passed ? "✓" : "✗"} {shortStep(v.stepId)}</span>
            ))}
          </div>)}
      </div>
      {all.length > 1 && (
        <div style={{ display: "flex", gap: 3, marginTop: 4 }}>
          {all.map((l) => (
            <button key={l.kind} onClick={() => setPick(l.kind)} title={`show the ${l.kind === "end" ? "end frame" : l.kind}`}
              style={{ ...small, padding: 0, width: 36, height: 36, overflow: "hidden", border: l === chosen ? "2px solid #2563eb" : "1px solid #d1d5db" }}>
              {l.preview ? <img src={l.preview.thumbUrl ?? l.preview.posterUrl} alt={l.kind} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 9 }}>{l.kind}</span>}
            </button>))}
        </div>)}
      {verdicts.filter((v) => v.passed === false && v.defect).slice(0, 1).map((v) => (
        <div key={v.stepId} style={{ marginTop: 3, fontSize: 11, color: "#b91c1c" }} title={v.defect!}>✗ {shortStep(v.stepId)}: {v.defect!.slice(0, 60)}{v.defect!.length > 60 ? "…" : ""}</div>))}
      {slice && <audio src={slice} controls preload="none" style={{ width: "100%", height: 28, marginTop: 4 }} title="this beat's voice" />}
      {layer.preview?.stripUrl && <img src={layer.preview.stripUrl} alt="strip" style={{ width: "100%", marginTop: 4, borderRadius: 2 }} />}
      <div style={{ marginTop: 4, fontWeight: 600 }}>{beat.position}. {beat.name ?? beat.beatId}</div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 3 }}>
        {o && <span style={pill(STATUS_COLOR[o.steps.find((s) => s.stepId === o.currentStep)?.status ?? "done"] ?? "#6b7280")}
          title="current step">{o.currentStep}</span>}
        {o && o.spentUsdCents > 0 && <span style={pill("#374151")} title="spent on this beat">{money(o.spentUsdCents)}</span>}
        {score != null && <span style={pill("#1d4ed8")} title="latest score">{score.toFixed(2)}</span>}
        {beat.production?.alignmentConfidence != null && beat.production.alignmentConfidence < 0.4 &&
          <span style={pill("#b45309")} title="alignment confidence — timing estimated">align {beat.production.alignmentConfidence.toFixed(2)}</span>}
      </div>
      {(p.onMark || p.onReroll || p.more?.length) && (
        <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
          {p.onMark && <>
            <button style={{ ...small, ...(mark === "ok" ? { background: "#dcfce7" } : {}) }} onClick={() => p.onMark!(mark === "ok" ? undefined : "ok")}>OK</button>
            <button style={{ ...small, ...(mark === "reroll" ? { background: "#fee2e2" } : {}) }} onClick={() => p.onMark!(mark === "reroll" ? undefined : "reroll")}>needs re-roll</button>
          </>}
          {p.onReroll && (layer.kind === "frame" || layer.kind === "clip") &&
            <button style={small} disabled={p.busy} onClick={() => p.onReroll!(layer.kind as "frame" | "clip")}>Re-roll {layer.kind}</button>}
          {p.more?.length ? (
            <details><summary style={{ cursor: "pointer", fontSize: 12 }}>More</summary>
              <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 3 }}>
                {p.more.map((m) => <button key={m.label} style={small} disabled={p.busy} onClick={m.run}>{m.label}</button>)}
              </div>
            </details>) : null}
        </div>
      )}
    </div>
  );
}

/** Every beat, grouped by segment — the inspector's first view. */
export function FilmstripView(p: {
  film: Filmstrip;
  marks?: Record<string, Mark | undefined>;
  onMark?: (beatId: string, m: Mark | undefined) => void;
  onReroll?: (beat: FilmstripBeat, layer: "frame" | "clip") => void;
  more?: (beat: FilmstripBeat) => { label: string; run: () => void }[];
  /** §4b: the scene the rough cut is playing — its card is ringed and scrolled into view. */
  activeBeatId?: string | null;
  busy?: boolean;
}) {
  const { film } = p;
  return (
    <div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8, fontSize: 13 }}>
        <span>run <code>{film.run.id.slice(0, 8)}</code> · {film.run.status}</span>
        <span>{money(film.run.spentUsdCents)} / {money(film.run.budgetUsdCents)}</span>
        <span>document: <b>{film.run.document.currentStep}</b></span>
        {film.run.awaitingGate && <span style={pill("#d97706")}>parked at {film.run.awaitingGate.stepId}</span>}
      </div>
      <DeliveredMedia film={film} />
      {film.segments.map((seg) => (
        <div key={seg.id} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>segment {seg.id}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {seg.beats.map((b) => (
              <BeatCard key={b.beatId} beat={b} active={p.activeBeatId === b.beatId} busy={p.busy} mark={p.marks?.[b.beatId]} voice={film.document.voice}
                onMark={p.onMark ? (m) => p.onMark!(b.beatId, m) : undefined}
                onReroll={p.onReroll ? (layer) => p.onReroll!(b, layer) : undefined}
                more={p.more?.(b)} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** The storyboard gate's review surface: the animatic, every beat, the marks' tally, and the RUN's Approve / Reject. */
export function ContactSheet(p: {
  film: Filmstrip;
  marks: Record<string, Mark | undefined>;
  onMark: (beatId: string, m: Mark | undefined) => void;
  onReroll?: (beat: FilmstripBeat, layer: "frame" | "clip") => void;
  /** `note` is the marks tally (marksNote) — pass it on as the gate decision's note. */
  onDecide: (approved: boolean, note: string) => void;
  deciding?: boolean;
}) {
  const beats = p.film.segments.flatMap((s) => s.beats);
  const c = markCounts(beats, p.marks);
  const gate = p.film.run.awaitingGate;
  const [active, setActive] = React.useState<string | null>(null);
  return (
    <div style={{ border: "1px solid #fcd34d", background: "#fffbeb", borderRadius: 8, padding: 12, marginBottom: 12 }}>
      {p.film.document.animaticUrl && (
        <div style={{ marginBottom: 10 }}>
          {/* §4b: the whole rough cut in one player, the filmstrip below following the scene it plays */}
          <RoughCutPlayer url={p.film.document.animaticUrl} beats={beats} onActive={setActive} />
          <div style={{ fontSize: 12, color: "#78350f", marginTop: 4 }}>
            The animatic plays the voice step's slice lengths — close to the final timing, not identical (presenter
            inserts and clip-driven timing are added when the voice is conformed to the clips).
          </div>
        </div>
      )}
      <FilmstripView film={p.film} marks={p.marks} onMark={p.onMark} onReroll={p.onReroll} busy={p.deciding} activeBeatId={active} />
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8 }}>
        <span style={{ fontSize: 13 }}>{c.ok} OK · <b style={{ color: c.reroll ? "#b91c1c" : undefined }}>{c.reroll} need re-roll</b> · {c.unmarked} unmarked</span>
        <button disabled={!gate || p.deciding} onClick={() => p.onDecide(true, marksNote(c))}
          style={{ background: "#16a34a", color: "#fff", border: 0, borderRadius: 4, padding: "6px 14px", cursor: "pointer" }}>
          {p.deciding ? "…" : `✓ Approve${gate ? ` ${gate.stepId}` : ""}`}
        </button>
        <button disabled={!gate || p.deciding} onClick={() => p.onDecide(false, marksNote(c))}
          style={{ background: "#eee", border: 0, borderRadius: 4, padding: "6px 14px", cursor: "pointer" }}>✕ Reject</button>
        {c.reroll > 0 && <span style={{ fontSize: 12, color: "#b91c1c" }}>{c.reroll} beat(s) marked for re-roll — re-roll them first, or approve anyway.</span>}
      </div>
    </div>
  );
}
